import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Link, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { formatInTimeZone } from 'date-fns-tz';
import { capitalize } from 'lodash';

import { CloseButton } from '../../../../../incidents/features/FilterPanel/FilterPanel.styles';
import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { ErrorIcon, WarningIcon } from '../../../../../shared/components/Icons';
import { LabelChip } from '../../../../../shared/components/LabelChip';
import { colorPalette, colorTokens } from '../../../../../shared/components/variables';
import { estimatedTimeFromNowWithin24Hours } from '../../../../../shared/utils/helpers/util';
import { useLiveSrsAvailabilities } from '../../../../hooks/useLiveSrsAvailabilities';
import { LiveAlertDetails } from '../../../../types/alert';
import {
  capitalizeFirstNameLastName,
  convertToSnakeCase,
  getStatusLabelProps,
  replaceUnderscoreWithSpacePascalCase,
} from '../../../PageHeading/utils';
import { SrsAvatarStatus } from '../../SrsAvatarStatus';
import { useAlertDetailsContext } from '../index';

import { AlertsDetailRow, Container } from './AlertsDetailModal.style';

export interface AlertsDetailModalProps {
  alertDetails: LiveAlertDetails;
  timeZone: string;
  isFetching: boolean;
}

export const AlertDetailsModal = () => {
  const { alertDetails, timeZone, isFetching } = useAlertDetailsContext();
  const navigate = useNavigate();
  const dateFormat = "EEE, M/dd/yyyy 'at' h:mm aa zzz";

  return (
    <Modal
      open={!isFetching && !!alertDetails}
      onClose={() => navigate('..')}
      data-testid="alert-details-modal"
      onClick={(e) => e.stopPropagation()}
      maxWidth="lg"
    >
      <Modal.Title>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap="10px">
            <Box display="flex" alignItems="center">
              {alertDetails?.highPriority ? <ErrorIcon /> : <WarningIcon />}
            </Box>
            <Text variant="h6" style={{ color: colorPalette.yellow200 }}>
              {alertDetails?.typeLabel}
            </Text>
          </Box>
          <CloseButton onClick={() => navigate('..')}>
            <Svg icon="cross" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Container>
          {alertDetails?.createdAt && (
            <ModalContentInnerRow
              title={'Created at'}
              value={`${formatInTimeZone(alertDetails?.createdAt, timeZone, dateFormat)} ${
                estimatedTimeFromNowWithin24Hours(alertDetails?.createdAt) || ''
              }`}
            />
          )}
          {alertDetails?.updatedAt && (
            <ModalContentInnerRow
              title={'Updated at'}
              value={`${formatInTimeZone(alertDetails?.updatedAt, timeZone, dateFormat)} ${
                estimatedTimeFromNowWithin24Hours(alertDetails?.updatedAt) || ''
              }`}
            />
          )}
          {alertDetails?.typeKey === 'b2b_conflict' && (
            <>
              <ModalContentInnerRow
                title={'Active Ride'}
                value={
                  <Link target="_blank" href={`/rides/${alertDetails?.metadata.activeRideSlug}`}>
                    <CopyToClipboard
                      toastMessage={'Slug Copied'}
                      text={alertDetails?.metadata.activeRideSlug}
                      variant="body1"
                      color={colorTokens.primary.light}
                      iconColor={colorTokens.text.secondary}
                      textStyle={{ textDecoration: 'underline' }}
                    />
                  </Link>
                }
              />
              <ModalContentInnerRow
                title={'ETA to Drop Off for the active ride'}
                value={
                  <>
                    <LabelChip
                      data-testid="ride-state"
                      isFetching={isFetching}
                      {...getStatusLabelProps(convertToSnakeCase(alertDetails?.metadata.activeRideState))}
                    />
                    {alertDetails?.metadata.activeRideEtaToDropoff
                      ? formatInTimeZone(alertDetails?.metadata.activeRideEtaToDropoff, timeZone, dateFormat)
                      : ''}
                  </>
                }
              />
              <ModalContentInnerRow
                title={'ETA to Pickup for upcoming ride in conflict'}
                value={
                  <>
                    <LabelChip
                      data-testid="ride-state"
                      isFetching={isFetching}
                      {...getStatusLabelProps(convertToSnakeCase(alertDetails?.metadata.upcomingRideState))}
                    />
                    {alertDetails?.metadata.etaToPickup
                      ? formatInTimeZone(alertDetails?.metadata.etaToPickup, timeZone, dateFormat)
                      : ''}
                  </>
                }
              />
            </>
          )}
          {alertDetails?.typeKey === 'late_eta_to_pickup' && (
            <ModalContentInnerRow
              title={'ETA to Pickup for the active ride'}
              value={
                alertDetails?.metadata.etaToPickup
                  ? formatInTimeZone(alertDetails?.metadata.etaToPickup, timeZone, dateFormat)
                  : ''
              }
            />
          )}
          {alertDetails?.typeKey === 'improper_arrival' && (
            <ModalContentInnerRow
              title={'Description'}
              value={`Driver ${alertDetails?.metadata.driverName} tapped I've Arrived ${
                alertDetails?.metadata.milesToUpcomingWaypoint
              } miles from ${replaceUnderscoreWithSpacePascalCase(
                alertDetails?.metadata.upcomingWaypointProcedureType
              )}`}
            />
          )}
          {alertDetails?.typeKey === 'unclaimed_prio' && (
            <>
              {alertDetails?.metadata.driverRemovalEvents.map((event) => (
                <ModalContentInnerRow
                  key={event.driverRemovalTime}
                  title={'Description'}
                  value={
                    <>
                      <Text>{event.driverRemovalTime}</Text>
                      <Text>
                        Driver {capitalizeFirstNameLastName(`${event.driverFirstName} ${event.driverLastName}`)} was
                        removed automatically due to {event.driverRemovalReasonDescription}
                      </Text>
                    </>
                  }
                />
              ))}
            </>
          )}

          {alertDetails?.status && <ModalContentInnerRow title={'Status'} value={capitalize(alertDetails?.status)} />}
          <ModalContentInnerRow
            title={'Assigned to'}
            isLastRow={true}
            value={
              alertDetails?.employeeId ? (
                <AssignedEmployeeStatus
                  employeeId={alertDetails?.employeeId}
                  employeeSrsAvailabilityStatus={alertDetails?.employeeSrsAvailabilityStatus || ''}
                  employeeFullName={capitalizeFirstNameLastName(
                    `${alertDetails?.employeeFirstName} ${alertDetails?.employeeLastName}`
                  )}
                  assignedAt={
                    (alertDetails?.employeeAssignedAt &&
                      `${formatInTimeZone(alertDetails?.employeeAssignedAt, timeZone, dateFormat)} ${
                        estimatedTimeFromNowWithin24Hours(alertDetails?.employeeAssignedAt) || ''
                      }`) ||
                    ''
                  }
                  employeeProfilePhotoUrl={alertDetails?.employeeProfilePhotoUrl || ''}
                />
              ) : (
                '-'
              )
            }
          />
        </Container>
      </Modal.Content>
    </Modal>
  );
};

interface ModalContentInnerRowProps {
  title: string;
  isLastRow?: boolean;
  value?: string | JSX.Element;
}

const ModalContentInnerRow: FC<ModalContentInnerRowProps> = ({ title, isLastRow = false, value }) => {
  return (
    <AlertsDetailRow $isLast={isLastRow}>
      <Box>
        <Text variant="body1" textColor="secondaryText">
          {title}
        </Text>
      </Box>
      <Box>
        <Text variant="body1" textColor="primaryText">
          {value || '-'}
        </Text>
      </Box>
    </AlertsDetailRow>
  );
};

interface AssignedEmployeeStatusProps {
  employeeId: number;
  employeeSrsAvailabilityStatus: string;
  employeeFullName: string;
  assignedAt: string;
  employeeProfilePhotoUrl: string;
}

const AssignedEmployeeStatus: FC<AssignedEmployeeStatusProps> = ({
  employeeId,
  employeeSrsAvailabilityStatus,
  employeeFullName,
  assignedAt,
  employeeProfilePhotoUrl,
}) => {
  const { getSrsLiveStatus } = useLiveSrsAvailabilities();

  return (
    <Box display="flex" alignItems="center" gap="10px">
      <SrsAvatarStatus
        fullName={employeeFullName}
        size={33}
        fontSize={14}
        statusSize={12}
        key={employeeId.toString()}
        status={getSrsLiveStatus(employeeId, employeeSrsAvailabilityStatus)}
        pictureUrl={employeeProfilePhotoUrl}
      />
      <Text variant="body1" textColor="primaryText">
        {employeeFullName}
      </Text>
      <Text variant="body1" textColor="secondaryText">
        {assignedAt}
      </Text>
    </Box>
  );
};
